import { HttpClient, HttpHeaders, HttpParams,HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '@env/environment';
import { SharedModule } from '@shared/shared.module';

const adminUrl = `${environment.apiBaseUrl}platform/admin`;
const baseUrl = `${environment.apiBaseUrl}platform`;
const url = `${environment.apiBaseUrl}platform`;
const symptomUrl = `${environment.apiBaseUrl}platform/symptoms/settings`;
const apisUrl = `${environment.apisBaseUrl}v1`;

const headers = new HttpHeaders({
  'x-no-notification': 'true'
});

@Injectable({
  providedIn: SharedModule
})
export class AdminService {
  featuresData: any;

  constructor(private http: HttpClient) { }

  getLDAPUsers(searchText?: string): Observable<any> {
    let params = new HttpParams();

    if (searchText) {
      params = params.append('text', searchText);
    }
    return this.http.get<any>(`${adminUrl}/ldap/search`, { params });
  }

  getAzureUsers(searchText?: string): Observable<any> {
    let params = new HttpParams();

    if (searchText) {
      params = params.append('text', searchText);
    }
    return this.http.get<any>(`${adminUrl}/azure/search`, { params });
  }

  summaryList(): Observable<AdminPanel.AdminSummary[]> {
    return this.http.get<AdminPanel.AdminSummary[]>(`${adminUrl}/summary`);
  }

  getSettings(key: string): Observable<any> {
    let params = new HttpParams();

    if (key) {
      params = params.append('key', key);
    }
    return this.http.get<any>(`${adminUrl}/settings`, { params });
  }

  getAzureSettings(): Observable<any> {
    return this.http.get<any>(`${adminUrl}/azure-settings`);
  }

  updateExclusionList(data: AdminSettings.Exclusion): Observable<any> {
    return this.http.put<any>(`${adminUrl}/gitwiser-exclusion`, data);
  }

  updateExecutiveDashboardPermission(data: AdminSettings.ExecutiveCompanyRole): Observable<any> {
    return this.http.put<any>(`${adminUrl}/executive-dashboard-company-roles`, data);
  }

  updateGitwiserGoals(data): Observable<any> {
    return this.http.put<any>(`${adminUrl}/gitwiser-pra-goals`, data);
  }

  updateExecutiveDashboardDateFilter(data: AdminSettings.ExecutiveDateFilter): Observable<any> {
    return this.http.put<any>(`${adminUrl}/executive-dashboard-date-filter`, data);
  }

  updateReanalyse(data: AdminSettings.Gitwiser): Observable<any> {
    return this.http.put<any>(`${adminUrl}/reanalyses`, data);
  }

  updateWorkTypeThreshold(data: AdminSettings.WorkTypeCalc): Observable<any> {
    return this.http.put<any>(`${adminUrl}/workTypeThreshold`, data);
  }

  updateExclusionLineCountThreshold(data: AdminSettings.WorkTypeCalc): Observable<any> {
    return this.http.put<any>(`${adminUrl}/exclusionLineCountThreshold`, data);
  }

  updateGitCommitTypeExclusion(data: AdminSettings.KeyLabel): Observable<any> {
    return this.http.put<any>(`${adminUrl}/git-commit-type-exclusion`, data);
  }

  updateImpactScoreThreshold(data: any): Observable<any> {
    return this.http.put<any>(`${adminUrl}/impact`, data);
  }

  updateReanalyseAgileSpace(data: AdminSettings.Gitwiser): Observable<any> {
    return this.http.put<any>(`${adminUrl}/agilespace-reanalyses`, data);
  }

  updateLDAPConfig(ldapConfig: AdminSettings.LDAP): Observable<any> {
    return this.http.put<any>(`${adminUrl}/ldap`, ldapConfig);
  }

  updateCodeQualityReportConf(data: any): Observable<any> {
    return this.http.put<any>(`${adminUrl}/tcqi-report-goal`, data);
  }

  updateCodeQualityConf(data: any): Observable<any> {
    return this.http.put<any>(`${adminUrl}/tcqi-configuration`, data);
  }

  updateAzureAd(azureData: AdminSettings.Azure): Observable<any> {
    return this.http.put<any>(`${adminUrl}/azure`, azureData);
  }

  sendAzureAd(azureData: AdminSettings.Azure): Observable<AdminSettings.Azure> {
    return this.http.post<AdminSettings.Azure>(`${adminUrl}/azure`, azureData);
  }

  updateMail(mailConfig: AdminSettings.Mail): Observable<any> {
    return this.http.put<any>(`${adminUrl}/mail`, mailConfig);
  }

  updateDateRange(data: AdminSettings.DefaultDateRange): Observable<any> {
    return this.http.put<any>(`${adminUrl}/date-range`, data);
  }

  sendTestMail(data: { recipient: string }): Observable<{ result: boolean }> {

    return this.http.post<{ result: boolean }>(`${adminUrl}/mail/test`, data, { headers });
  }

  getAddons(type: string, key: string): Observable<any> {
    let params = new HttpParams();

    if (type) {
      params = params.append('type', type);
    }

    if (key) {
      params = params.append('key', key);
    }
    return this.http.get<any>(`${adminUrl}/addons`, { params });
  }

  updateAddons(type: string, key: string, data: any): Observable<any> {
    let params = new HttpParams();

    if (type) {
      params = params.append('type', type);
    }

    if (key) {
      params = params.append('key', key);
    }

    return this.http.put<any>(`${adminUrl}/addons`, data, { params });
  }

  updateServer(serverConfig: AdminSettings.Server): Observable<any> {
    return this.http.put<any>(`${adminUrl}/base-url`, serverConfig);
  }

  updateCompanyRole(data: AdminSettings.CompanyRole): Observable<any> {
    return this.http.put<any>(`${adminUrl}/company-role`, data);
  }

  updateFilteredCompanyRoles(data: AdminSettings.FilteredCompanyRoles): Observable<any> {
    return this.http.put<any>(`${adminUrl}/filtered-company-roles`, data);
  }

  updateCompanyRoleMapping(data: AdminSettings.FilteredCompanyRoles): Observable<any> {
    return this.http.put<any>(`${adminUrl}/company-role-mapping`, data);
  }

  updateScorecardCompanyRoles(data): Observable<any> {
    return this.http.put<any>(`${adminUrl}/restricted-company-roles`, data);
  }

  getRoleEnum(): Observable<any> {
    return this.http.get<any>(`${baseUrl}/enum/profile-company-roles`)
  }

  updateContributionCalendarRoles(data: AdminSettings.ContributionCalendarRoles): Observable<any> {
    return this.http.put<any>(`${adminUrl}/contribution-calendar-roles`, data);
  }

  getCustomization(): Observable<any> {
    return this.http.get<any>(`${adminUrl}/customization`)
  }

  getLogs(serviceName: string): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (serviceName) {
      params = params.append('serviceName', serviceName);
    }
    return this.http.get(`${adminUrl}/logs`, { params, responseType: 'blob', observe: 'response' })
  }

  servicesList(): Observable<any> {
    return this.http.get(`${adminUrl}/logs/services`)
  }

  updateDeploymentGoals(data): Observable<any> {
    return this.http.put<any>(`${baseUrl}/gitwiser/settings/default-deployment-analytics`, data);
  }

  getLicenseKey(): Observable<any> {
    return this.http.get<any>(`${url}/licence`);
  }

  updateLicenseKey(data: AdminSettings.LicenseKey): Observable<any> {
    return this.http.post<any>(`${url}/licence`, data);
  }

  getSymptoms(): Observable<any> {
    return this.http.get<any>(`${symptomUrl}`);
  }

  createSymptoms(data: any): Observable<any> {
    return this.http.post<any>(`${symptomUrl}`, data);
  }

  getFeatures(): Observable<any> {
    return this.http.get<any>(`${adminUrl}/features`);
  }

  updateFeatures(data: any): Observable<any> {
    return this.http.post<any>(`${adminUrl}/features`, data);
  }

  testConnectionLDAP(data: any): Observable<any> {
    return this.http.post<any>(`${adminUrl}/ldap/test-connection`, data);
  }

  updateOkta(oktaData: AdminSettings.Okta): Observable<any> {
    return this.http.put<any>(`${adminUrl}/okta`, oktaData);
  }

  sendOkta(oktaData: AdminSettings.Okta): Observable<AdminSettings.Okta> {
    return this.http.post<AdminSettings.Okta>(`${adminUrl}/okta`, oktaData);
  }

  getOktaSettings(): Observable<any> {
    return this.http.get<any>(`${adminUrl}/okta-settings`);
  }

  getLDAPGroups(): Observable<any> {
    return this.http.get<any>(`${adminUrl}/ldap/groups`);
  }

  createLDAPGroupsOfUsers(data: any): Observable<any> {
    return this.http.post<any>(`${adminUrl}/ldap/group/users`, data, { headers });
  }

  createLDAPUsers(data: any): Observable<any> {
    return this.http.post<any>(`${adminUrl}/ldap/users`, data);
  }

  createLDAPSync(): Observable<any> {
    return this.http.post<any>(`${adminUrl}/ldap/sync`, {}, { headers });
  }
  getApiToken(): Observable<SecurityModel.Payload> {
    return this.http.get<SecurityModel.Payload>(`${apisUrl}/api-keys`);
  }

  postApiToken(token: SecurityModel.Token): Observable<any> {
    return this.http.post<any>(`${apisUrl}/api-keys`, token);
  }

  deleteApiToken(id: string): Observable<any> {
    return this.http.delete<any>(`${apisUrl}/api-keys/${id}`);
  }

  updateExclusionForm(data: {
    exclusions: any;
    resetExistingAnalyses: boolean;
    deleteExcludedCommits: boolean;
  }): Observable<any> {
    return this.http.put<any>(`${adminUrl}/global-git-exclusion`, data);
  }

  updateExcludeForm(data: AdminSettings.ExclusionPayload): Observable<any> {
    return this.http.put<any>(`${adminUrl}/exclude-roles`, data);
  }

  getCompanyRoles(): Observable<any[]> {
    return this.http.get<any[]>(`${baseUrl}/developers/company-roles`);
  }

  updateTimeZone(data: any): Observable<any[]>{
    return this.http.post<any>(`${adminUrl}/settings/time-zone`, data);
  }

  getTimeZone(): Observable<any> {
    return this.http.get<any>(`${adminUrl}/settings/time-zone`);
  }

  getDefaultTimeZone(): Observable<any> {
    return this.http.get<any>(`${adminUrl}/settings/time-zone/default-data/time-zones`);
  }

  getDefaultWorkingHours(): Observable<any> {
    return this.http.get<any>(`${adminUrl}/settings/time-zone/default-data/working-hours`);
  }

  updatePullRequestSizeBranchGroup(data: any): Observable<any> {
    return this.http.put<any>(`${adminUrl}/pull-request-size-branch-group`, data);
  }

  updateAgileSpaceTypeMapping(typeMapping: any): Observable<any> {
    return this.http.put<any>(`${adminUrl}/agile-space-type-mapping`, {typeMapping});
  }

  updateJiraTypeMapping(settings: any): Observable<any> {
    return this.http.put<any>(`${adminUrl}/jira-type-mapping`, settings);
  }

  updateAzureTypeMapping(data: any): Observable<any> {
    return this.http.put<any>(`${adminUrl}/azure-type-mapping`, data);
  }
  updateTypeMappingExclusion(data: any): Observable<any> {
    return this.http.put<any>(`${adminUrl}/type-mapping-exclusion`, data);
  }


  clearRequestSubmit(){
    return this.http.post<any>(`${baseUrl}/reset-pull-analytics-process`, {});
  }
}
